/**
 * Local, no-AI coaching: turn a SessionReport (+ optional seed raga record)
 * into the same CoachingNote the UI renders. Everything here is derived from
 * stats we already compute plus the raga's own swaras, so it runs
 * client-side, offline, for every raga. The seed record, when present, adds
 * the musicology the numbers can't (gamaka/swara roles, arohana/avarohana).
 */
import { JI_RATIOS } from './music.js';
const SWARA_NAMES = {
    S: 'Shadja (S)', r: 'Shuddha Rishabha (r)', R: 'Rishabha (R)', g: 'Sadharana Gandhara (g)',
    G: 'Gandhara (G)', m: 'Shuddha Madhyama (m)', M: 'Prati Madhyama (M)', P: 'Panchama (P)',
    d: 'Shuddha Dhaivata (d)', D: 'Dhaivata (D)', n: 'Kaishiki Nishada (n)', N: 'Nishada (N)',
};
const name = (s) => SWARA_NAMES[s] ?? s;
/** Ensures `s` ends with exactly one period. Curated seed text (gamaka/per-swara
 * notes) is written as a complete sentence already ending in ".", so blindly
 * appending another `'.'` after splicing it into a longer sentence doubles it up
 * ("...essential to the raga's identity.."); this strips any existing trailing
 * period(s) first so callers can always just call `withPeriod(...)` instead of
 * hand-appending `'.'`. */
function withPeriod(s) {
    return `${s.replace(/\.+$/, '')}.`;
}
function ascending(allowed) {
    return [...allowed].sort((a, b) => JI_RATIOS[a] - JI_RATIOS[b]);
}
/** The allowed swaras just below and above `sym` in pitch (for a neighbour drill). */
function neighbours(sym, allowed) {
    const asc = ascending(allowed);
    const i = asc.indexOf(sym);
    return {
        ...(i > 0 ? { below: asc[i - 1] } : {}),
        ...(i >= 0 && i < asc.length - 1 ? { above: asc[i + 1] } : {}),
    };
}
/** The gamaka-profile note for a swara (12-symbol keyed), if any. */
function gamakaNoteFor(sym, raga) {
    return raga.gamaka_profile?.[sym]?.note;
}
/** The curated per-swara note for a swara, if any. */
function perSwaraNoteFor(sym, raga) {
    return raga.per_swara_notes?.find((t) => t.swara === sym)?.note;
}
/** The musicological "why" (gamaka role) — gamaka notes first, since that's what explains a wobble. */
function whyRoleFor(sym, raga) {
    if (!raga)
        return undefined;
    return gamakaNoteFor(sym, raga) ?? perSwaraNoteFor(sym, raga);
}
/** The actionable practice tip — the curated per-swara technique first, falling back to the gamaka note. */
function tipRoleFor(sym, raga) {
    if (!raga)
        return undefined;
    return perSwaraNoteFor(sym, raga) ?? gamakaNoteFor(sym, raga);
}
/** Reverse `swara_variants` (12-symbol → swarasthana) to swarasthana → 12-symbol, for readable display. */
function displaySymbols(raga) {
    const rev = {};
    for (const [symbol, st] of Object.entries(raga.swara_variants)) {
        if (!(st in rev))
            rev[st] = symbol;
    }
    return rev;
}
/** Render a swarasthana sequence readably — 12-symbol when resolvable via this raga, else the swarasthana name. */
function displaySwaras(sts, raga) {
    const rev = displaySymbols(raga);
    return sts.map((st) => rev[st] ?? st);
}
function focusItem(s, ctx, isWeakest, audience) {
    const dir = s.meanCents < -8 ? 'flat' : s.meanCents > 8 ? 'sharp' : 'unsteady';
    const wide = s.stdCents > 30;
    const whyRole = whyRoleFor(s.symbol, ctx.raga);
    const tipRole = tipRoleFor(s.symbol, ctx.raga);
    const { below, above } = neighbours(s.symbol, ctx.allowed);
    const around = below && above ? `${below}–${s.symbol}–${above}` : `around ${s.symbol}`;
    const whyBits = audience === 'child'
        ? [
            isWeakest ? `${name(s.symbol)} was your least-settled note` : `${name(s.symbol)} also needs attention`,
            dir === 'flat'
                ? 'and sat a little flat'
                : dir === 'sharp'
                    ? 'and sat a little sharp'
                    : 'and its pitch wandered around',
        ]
        : [
            isWeakest
                ? `${name(s.symbol)} was your least-settled note (${Math.round(s.inTunePct)}% centred)`
                : `${name(s.symbol)} also needs attention (${Math.round(s.inTunePct)}% centred)`,
            dir === 'flat'
                ? `and sat about ${Math.abs(Math.round(s.meanCents))}¢ flat`
                : dir === 'sharp'
                    ? `and sat about ${Math.round(s.meanCents)}¢ sharp`
                    : `and its pitch wandered (±${Math.round(s.stdCents)}¢)`,
        ];
    if (whyRole)
        whyBits.push(`— in ${ctx.ragaName}, ${whyRole}`);
    const tip = dir === 'flat'
        ? `Hold it long against the drone — it's sagging; sing ${around} slowly and settle ${s.symbol} as a plain note before adding gamaka.`
        : dir === 'sharp'
            ? `Aim slightly under it and anchor it against Sa; sing ${around} slowly, letting ${s.symbol} rest a touch lower.`
            : `Sing ${s.symbol} as a long, steady plain note to find its centre before oscillating; then reintroduce the gamaka from that anchored pitch.`;
    // Avoid repeating the same role sentence in both `why` and `tip`.
    const roleTip = tipRole && tipRole !== whyRole
        ? ` ${withPeriod(`${tipRole[0].toUpperCase()}${tipRole.slice(1)}`)}`
        : '';
    return {
        area: name(s.symbol),
        why: withPeriod(whyBits.join(' ')),
        tip: (wide && dir !== 'unsteady' ? tip + ' Keep the pitch centre still.' : tip) + roleTip,
        swara: s.symbol,
    };
}
/**
 * Up to ~2 gamaka focus items from the session's flagged note-events (Task 4),
 * deduped by swara — first flag wins so a swara doesn't produce two items even
 * if it was flagged both ways across the take.
 */
function gamakaFocusItems(report, ctx) {
    const seen = new Set();
    const items = [];
    for (const flag of report.gamaka.flagged) {
        if (seen.has(flag.swara) || items.length >= 2)
            continue;
        seen.add(flag.swara);
        const swaraName = name(flag.swara);
        if (flag.verdict === 'held-flat') {
            const roleNote = ctx.raga?.gamaka_profile?.[flag.swara]?.note ?? `this swara should oscillate in ${ctx.ragaName}`;
            items.push({
                area: swaraName,
                why: withPeriod(`${swaraName} was in tune but held flat — ${roleNote}`),
                tip: `Add the gamaka: oscillate ${flag.swara} gently rather than holding it steady.`,
                swara: flag.swara,
            });
        }
        else {
            items.push({
                area: swaraName,
                why: `${swaraName} wandered — in ${ctx.ragaName} it should stay steady.`,
                tip: `Hold ${flag.swara} steadier; let it rest without oscillating.`,
                swara: flag.swara,
            });
        }
    }
    return items;
}
function warmup(ctx) {
    const raga = ctx.raga;
    const asc = raga?.arohana.length ? displaySwaras(raga.arohana, raga) : ascending(ctx.allowed);
    const desc = raga?.avarohana.length ? displaySwaras(raga.avarohana, raga) : [...ascending(ctx.allowed)].reverse();
    return (`Against the tanpura, sing slowly up and down ${ctx.ragaName}: ${asc.join(' ')} — and back ${desc.join(' ')}, ` +
        `holding each swara about 4 beats until it locks in tune.`);
}
export function localCoaching(report, ctx, opts) {
    const audience = opts?.audience ?? 'grownup';
    if (report.voicedSamples === 0) {
        return { encouragement: 'No audio yet — press Start practice and sing.', strengths: [], focus: [], warmup: '' };
    }
    const p = report.inRagaPct;
    const encouragement = p >= 90
        ? `Lovely — you stayed inside ${ctx.ragaName} almost the whole way. Now we polish the details.`
        : p >= 75
            ? `Well done — you stayed inside ${ctx.ragaName} most of the time. A few landing points to refine.`
            : p >= 50
                ? `Good effort — you held ${ctx.ragaName} about half the time. Let's steady a couple of notes.`
                : `Keep at it — the pitch wandered outside ${ctx.ragaName} often, which is normal early on. Here's where to aim.`;
    const strengths = report.strongest
        .filter((s) => s.inTunePct >= 70)
        .slice(0, 3)
        .map((s) => audience === 'child'
        ? `${name(s.symbol)} is a dependable anchor.`
        : `${name(s.symbol)} is a dependable anchor — ${Math.round(s.inTunePct)}% centred (±${Math.round(s.stdCents)}¢).`);
    const intonationFocus = report.weakest
        .filter((s) => s.inTunePct < 70)
        .slice(0, 3)
        .map((s, i) => focusItem(s, ctx, i === 0, audience));
    const focus = [...intonationFocus, ...gamakaFocusItems(report, ctx)];
    return { encouragement, strengths, focus, warmup: warmup(ctx) };
}
//# sourceMappingURL=local-coach.js.map